import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { apiFetch } from "../../utils/api";
import { ReactSortable } from "react-sortablejs";
import { ChevronDown } from "lucide-react";
import { useSettingsSection } from "./SettingsContext";
import { SettingsSectionSkeleton } from "./SettingsSkeleton";
function ToggleCard({
  label,
  description,
  checked,
  onToggle,
  disabled = false
}) {
  return <button type="button" onClick={() => {
    if (!disabled) onToggle();
  }} disabled={disabled} className={`w-full rounded-xl border px-4 py-4 text-left transition-colors ${disabled ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-500" : checked ? "border-blue-500 bg-blue-50 text-gray-900 dark:border-blue-400 dark:bg-blue-500/10 dark:text-gray-100" : "border-gray-300 bg-white text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-base font-semibold">{label}</div>
          {description ? <div className="mt-1 text-sm opacity-80">{description}</div> : null}
        </div>
        <div className={`mt-1 flex h-8 w-14 shrink-0 items-center rounded-full px-1 transition-colors ${checked ? "bg-blue-600 dark:bg-blue-500" : "bg-gray-300 dark:bg-gray-600"}`}>
          <div className={`h-6 w-6 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-6" : "translate-x-0"}`} />
        </div>
      </div>
    </button>;
}
function SelectCard({
  label,
  description,
  value,
  onChange,
  options
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  return <div className="rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-700">
      <label className="block text-base font-semibold text-gray-800 dark:text-gray-100">
        {label}
      </label>
      {description ? <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p> : null}
      <div className="relative mt-3">
        <select value={value} onChange={onChange} onFocus={() => setIsExpanded(true)} onBlur={() => setIsExpanded(false)} onMouseDown={() => setIsExpanded(prev => !prev)} onKeyDown={e => {
        if (e.key === "Escape" || e.key === "Tab") {
          setIsExpanded(false);
        }
        if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown" || e.key === "ArrowUp") {
          setIsExpanded(true);
        }
      }} className="block w-full appearance-none rounded-xl border border-gray-300 px-4 py-4 pr-14 text-base dark:border-gray-600 dark:bg-gray-800 dark:text-white">
          {options.map(option => <option key={option.value} value={option.value}>
              {option.label}
            </option>)}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500 dark:text-gray-300">
          <ChevronDown size={18} strokeWidth={2.25} className={`transition-transform duration-150 ${isExpanded ? "rotate-180" : ""}`} />
        </div>
      </div>
    </div>;
}
function StepNumberField({
  label,
  value,
  onChange,
  min = 1
}) {
  const numericValue = Number(value) || min;
  const updateValue = nextValue => {
    const safeValue = Math.max(min, Number(nextValue) || min);
    onChange({
      target: {
        value: String(safeValue)
      }
    });
  };
  return <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
      {label}
      <div className="mt-2 flex items-center gap-2">
        <button type="button" onClick={() => updateValue(numericValue - 1)} className="flex h-12 w-12 items-center justify-center rounded-xl border border-gray-300 bg-gray-100 text-xl font-semibold text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
          -
        </button>
        <input type="number" min={min} inputMode="numeric" value={value} onChange={onChange} className="block w-full appearance-none rounded-xl border border-gray-300 px-4 py-3 text-center text-base dark:border-gray-600 dark:bg-gray-800 dark:text-white" />
        <button type="button" onClick={() => updateValue(numericValue + 1)} className="flex h-12 w-12 items-center justify-center rounded-xl border border-gray-300 bg-gray-100 text-xl font-semibold text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
          +
        </button>
      </div>
    </label>;
}
function ThresholdCard({
  label,
  description,
  firstLabel,
  firstValue,
  onFirstChange,
  secondLabel,
  secondValue,
  onSecondChange,
  warning
}) {
  return <div className="rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-700">
      <label className="block text-base font-semibold text-gray-800 dark:text-gray-100">
        {label}
      </label>
      {description ? <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p> : null}
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <StepNumberField label={firstLabel} value={firstValue} onChange={onFirstChange} />
        <StepNumberField label={secondLabel} value={secondValue} onChange={onSecondChange} />
      </div>
      {warning ? <p className="mt-2 text-xs text-amber-600 dark:text-amber-300">{warning}</p> : null}
    </div>;
}
export default function TicketHandling({
  stationId,
  forcedSubTab = null,
  hideSubTabBar = false,
  sectionMode = null
}) {
  // Display Settings
  const [uiScale, setUiScale] = useState("medium");
  const [showCategory, setShowCategory] = useState(true);
  const [groupByCategory, setGroupByCategory] = useState(false);
  const [combineCondiments, setCombineCondiments] = useState(false);
  const [enableFireTimer, setEnableFireTimer] = useState(false);
  const [separateItemprepsInSummary, setSeparateItemprepsInSummary] = useState(false);
  const [hidePrepsInSummary, setHidePrepsInSummary] = useState(false);

  // NEW: Ticket color thresholds (minutes)
  const [ticketColorYellowAfter, setTicketColorYellowAfter] = useState(5);
  const [ticketColorRedAfter, setTicketColorRedAfter] = useState(10);

  // NEW: Course color thresholds (minutes)
  const [courseColorYellowAfter, setCourseColorYellowAfter] = useState(7);
  const [courseColorRedAfter, setCourseColorRedAfter] = useState(12);

  // Ticket Behavior Settings
  const [mergeMode, setMergeMode] = useState("separate");
  const [isEncore, setIsEncore] = useState(false);
  const [ticketFirePromotionMode, setTicketFirePromotionMode] = useState("none");

  // Coursing Settings
  const [coursing, setCoursing] = useState(false);
  const [syncCoursesWithExpo, setSyncCoursesWithExpo] = useState(false);
  const [firingSyncMethod, setFiringSyncMethod] = useState("none");
  const [syncFiringWithExpo, setSyncFiringWithExpo] = useState(false);
  const [syncFiringWithAllStations, setSyncFiringWithAllStations] = useState(false);
  const [bumpingSyncMethod, setBumpingSyncMethod] = useState("none");
  const [displayBumpStatusOfOtherUnits, setDisplayBumpStatusOfOtherUnits] = useState(false);
  const [displayBumpStatusOfOtherUnitsTicket, setDisplayBumpStatusOfOtherUnitsTicket] = useState(true);
  const [displayBumpStatusOfOtherUnitsCourse, setDisplayBumpStatusOfOtherUnitsCourse] = useState(false);
  const [displayBumpStatusOfOtherUnitsItem, setDisplayBumpStatusOfOtherUnitsItem] = useState(false);
  const [parkingSyncMethod, setParkingSyncMethod] = useState("none");
  const [syncParkingWithExpo, setSyncParkingWithExpo] = useState(false);
  const [syncParkingWithAllStations, setSyncParkingWithAllStations] = useState(false);
  const [ticketLocationSyncMethod, setTicketLocationSyncMethod] = useState("none");
  const [ticketPageSyncMethod, setTicketPageSyncMethod] = useState("none");
  const [hideCourse, setHideCourse] = useState(false);
  const [hideBumpedCourses, setHideBumpedCourses] = useState(false);
  const [showDetailedTimingData, setShowDetailedTimingData] = useState(false);
  const [ticketColorByCourse, setTicketColorByCourse] = useState(false);
  const [useCourseSeparator, setUseCourseSeparator] = useState(false);
  const [courseSeparatorTop, setCourseSeparatorTop] = useState(false);
  const [displayCourseLabels, setDisplayCourseLabels] = useState(false);
  const [isExpoStation, setIsExpoStation] = useState(false);
  const [isSyncGroupsOpen, setSyncGroupsOpen] = useState(false);
  const [syncGroupStations, setSyncGroupStations] = useState([]);
  const [syncGroups, setSyncGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [syncGroupsLoaded, setSyncGroupsLoaded] = useState(false);
  const initialRef = useRef(null);
  const savedValuesRef = useRef(null);
  const courseLabelsRef = useRef({});

  // Modal state for the full-screen overlay
  const [isCourseSetupOpen, setCourseSetupOpen] = useState(false);

  // Categories (ranked + placeholder rows)
  const [categories, setCategories] = useState([]);
  // Unranked categories (from items table)
  const [unrankedCategories, setUnrankedCategories] = useState([]);
  const [subTab, setSubTab] = useState(forcedSubTab || "display");
  const normalizeCategoriesForCompare = useCallback(list => {
    const normalized = (list || []).filter(c => c && c.name && !c.placeholder).map(c => ({
      name: String(c.name).trim(),
      ranking: Number(c.ranking) || 0,
      category_group: Number(c.category_group) || 0
    })).sort((a, b) => a.category_group - b.category_group || a.ranking - b.ranking || a.name.localeCompare(b.name));
    return JSON.stringify(normalized);
  }, []);
  const normalizeCategoryName = useCallback(value => {
    return String(value || "").trim().replace(/\s+/g, " ").toLowerCase();
  }, []);
  const normalizeSyncGroupIds = useCallback(value => {
    const source = Array.isArray(value) ? value : value == null || value === "" ? [] : (() => {
      try {
        return JSON.parse(value);
      } catch {
        return [];
      }
    })();
    return Array.from(new Set((source || []).map(entry => Number(entry)).filter(entry => Number.isFinite(entry) && entry > 0))).sort((a, b) => a - b);
  }, []);
  const buildSyncGroupClone = useCallback((station, groupId) => ({
    id: `${groupId}-${station.station_id}-${Math.random().toString(36).slice(2, 9)}`,
    station_id: Number(station.station_id),
    name: station.name,
    role: station.role
  }), []);
  const normalizeSyncGroupsState = useCallback(groups => {
    return (groups || []).map(group => {
      const groupId = Number(group?.groupId);
      if (!Number.isFinite(groupId) || groupId <= 0) return null;
      const seen = new Set();
      const stations = (group?.stations || []).filter(station => {
        const stationId = Number(station?.station_id);
        if (!Number.isFinite(stationId) || seen.has(stationId)) return false;
        seen.add(stationId);
        return true;
      }).map(station => buildSyncGroupClone(station, groupId));
      return {
        groupId,
        stations
      };
    }).filter(Boolean).sort((a, b) => a.groupId - b.groupId);
  }, [buildSyncGroupClone]);
  const buildSyncGroupsFromStations = useCallback(stations => {
    const groupIds = Array.from(new Set((stations || []).flatMap(station => normalizeSyncGroupIds(station.group_ids)))).sort((a, b) => a - b);
    return normalizeSyncGroupsState(groupIds.map(groupId => ({
      groupId,
      stations: (stations || []).filter(station => normalizeSyncGroupIds(station.group_ids).includes(groupId)).map(station => buildSyncGroupClone(station, groupId))
    })));
  }, [buildSyncGroupClone, normalizeSyncGroupIds, normalizeSyncGroupsState]);
  const buildSyncGroupMemberships = useCallback(groups => {
    const memberships = {};
    (groups || []).forEach(group => {
      const groupId = Number(group?.groupId);
      if (!Number.isFinite(groupId) || groupId <= 0) return;
      (group?.stations || []).forEach(station => {
        const stationId = Number(station?.station_id);
        if (!Number.isFinite(stationId)) return;
        if (!memberships[stationId]) memberships[stationId] = [];
        memberships[stationId].push(groupId);
      });
    });
    Object.keys(memberships).forEach(stationId => {
      memberships[stationId] = Array.from(new Set(memberships[stationId])).sort((a, b) => a - b);
    });
    return memberships;
  }, []);
  const buildSavedValues = useCallback(() => {
    const summaryMode = hidePrepsInSummary ? "hidden" : separateItemprepsInSummary ? "separate" : "combined";
    return {
      uiScale: uiScale || "medium",
      showCategory: !!showCategory,
      groupByCategory: !!groupByCategory,
      combineCondiments: !!combineCondiments,
      enableFireTimer: !!enableFireTimer,
      summaryMode,
      ticketColorYellowAfter: Number(ticketColorYellowAfter) || 0,
      ticketColorRedAfter: Number(ticketColorRedAfter) || 0,
      courseColorYellowAfter: Number(courseColorYellowAfter) || 0,
      courseColorRedAfter: Number(courseColorRedAfter) || 0,
      mergeMode: mergeMode || "separate",
      isEncore: !!isEncore,
      ticketFirePromotionMode: ticketFirePromotionMode === "any_fired" || ticketFirePromotionMode === "all_fired" ? ticketFirePromotionMode : "none",
      coursing: !!coursing,
      syncCoursesWithExpo: !!syncCoursesWithExpo,
      firingSyncMethod,
      syncFiringWithExpo: !!syncFiringWithExpo,
      syncFiringWithAllStations: !!syncFiringWithAllStations,
      bumpingSyncMethod,
      displayBumpStatusOfOtherUnits: !!displayBumpStatusOfOtherUnits,
      displayBumpStatusOfOtherUnitsTicket: !!displayBumpStatusOfOtherUnitsTicket,
      displayBumpStatusOfOtherUnitsCourse: !!displayBumpStatusOfOtherUnitsCourse,
      displayBumpStatusOfOtherUnitsItem: !!displayBumpStatusOfOtherUnitsItem,
      parkingSyncMethod,
      syncParkingWithExpo: !!syncParkingWithExpo,
      syncParkingWithAllStations: !!syncParkingWithAllStations,
      ticketLocationSyncMethod,
      ticketPageSyncMethod,
      hideCourse: !!hideCourse,
      hideBumpedCourses: !!hideBumpedCourses,
      showDetailedTimingData: !!showDetailedTimingData,
      ticketColorByCourse: !!ticketColorByCourse,
      useCourseSeparator: !!useCourseSeparator,
      courseSeparatorTop: !!courseSeparatorTop,
      displayCourseLabels: !!displayCourseLabels,
      syncGroups: buildSyncGroupMemberships(syncGroups),
      courseLabels: Object.fromEntries((categories || []).filter(c => c?.placeholder && Number.isFinite(Number(c.category_group))).map(c => [String(Number(c.category_group)), String(c.name || "").trim()]).filter(([, value]) => value)),
      categories: (categories || []).filter(c => c && c.name && !c.placeholder && Number.isFinite(Number(c.category_group)) && Number(c.category_group) > 0).map(c => ({
        name: c.name,
        ranking: c.ranking,
        category_group: c.category_group
      }))
    };
  }, [uiScale, showCategory, groupByCategory, combineCondiments, enableFireTimer, hidePrepsInSummary, separateItemprepsInSummary, ticketColorYellowAfter, ticketColorRedAfter, courseColorYellowAfter, courseColorRedAfter, mergeMode, isEncore, ticketFirePromotionMode, coursing, syncCoursesWithExpo, firingSyncMethod, syncFiringWithExpo, syncFiringWithAllStations, bumpingSyncMethod, displayBumpStatusOfOtherUnits, displayBumpStatusOfOtherUnitsTicket, displayBumpStatusOfOtherUnitsCourse, displayBumpStatusOfOtherUnitsItem, parkingSyncMethod, syncParkingWithExpo, syncParkingWithAllStations, ticketLocationSyncMethod, ticketPageSyncMethod, hideCourse, hideBumpedCourses, showDetailedTimingData, ticketColorByCourse, useCourseSeparator, courseSeparatorTop, displayCourseLabels, syncGroups, buildSyncGroupMemberships, categories]);
  const buildSnapshotFromValues = useCallback(values => {
    if (!values) return null;
    return {
      uiScale: values.uiScale || "medium",
      showCategory: !!values.showCategory,
      groupByCategory: !!values.groupByCategory,
      combineCondiments: !!values.combineCondiments,
      enableFireTimer: !!values.enableFireTimer,
      summaryMode: values.summaryMode,
      ticketColorYellowAfter: Number(values.ticketColorYellowAfter) || 0,
      ticketColorRedAfter: Number(values.ticketColorRedAfter) || 0,
      courseColorYellowAfter: Number(values.courseColorYellowAfter) || 0,
      courseColorRedAfter: Number(values.courseColorRedAfter) || 0,
      mergeMode: values.mergeMode || "separate",
      isEncore: !!values.isEncore,
      ticketFirePromotionMode: values.ticketFirePromotionMode === "any_fired" || values.ticketFirePromotionMode === "all_fired" ? values.ticketFirePromotionMode : "none",
      coursing: !!values.coursing,
      syncCoursesWithExpo: !!values.syncCoursesWithExpo,
      firingSyncMethod: values.firingSyncMethod || "none",
      syncFiringWithExpo: !!values.syncFiringWithExpo,
      syncFiringWithAllStations: !!values.syncFiringWithAllStations,
      bumpingSyncMethod: values.bumpingSyncMethod || "none",
      displayBumpStatusOfOtherUnits: !!values.displayBumpStatusOfOtherUnits,
      displayBumpStatusOfOtherUnitsTicket: !!values.displayBumpStatusOfOtherUnitsTicket,
      displayBumpStatusOfOtherUnitsCourse: !!values.displayBumpStatusOfOtherUnitsCourse,
      displayBumpStatusOfOtherUnitsItem: !!values.displayBumpStatusOfOtherUnitsItem,
      parkingSyncMethod: values.parkingSyncMethod || "none",
      syncParkingWithExpo: !!values.syncParkingWithExpo,
      syncParkingWithAllStations: !!values.syncParkingWithAllStations,
      ticketLocationSyncMethod: values.ticketLocationSyncMethod || "none",
      ticketPageSyncMethod: values.ticketPageSyncMethod || "none",
      hideCourse: !!values.hideCourse,
      hideBumpedCourses: !!values.hideBumpedCourses,
      showDetailedTimingData: !!values.showDetailedTimingData,
      ticketColorByCourse: !!values.ticketColorByCourse,
      useCourseSeparator: !!values.useCourseSeparator,
      courseSeparatorTop: !!values.courseSeparatorTop,
      displayCourseLabels: values.displayCourseLabels !== false,
      syncGroupsHash: JSON.stringify(values.syncGroups || {}),
      courseLabelsHash: JSON.stringify(values.courseLabels || {}),
      categoriesHash: normalizeCategoriesForCompare(values.categories || [])
    };
  }, [normalizeCategoriesForCompare]);
  const buildSnapshot = useCallback(() => {
    return buildSnapshotFromValues(buildSavedValues());
  }, [buildSavedValues, buildSnapshotFromValues]);

  // ===== Helpers =====
  const getExistingCourseIds = useCallback(() => {
    return Array.from(new Set(categories.map(c => Number(c.category_group)).filter(n => Number.isFinite(n) && n > 0))).sort((a, b) => a - b);
  }, [categories]);
  const maxCourseId = useMemo(() => {
    const ids = getExistingCourseIds();
    return ids.length ? Math.max(...ids) : 0;
  }, [getExistingCourseIds]);
  const maxSyncGroupId = useMemo(() => {
    const ids = (syncGroups || []).map(group => Number(group.groupId)).filter(groupId => Number.isFinite(groupId));
    return ids.length ? Math.max(...ids) : 0;
  }, [syncGroups]);

  // Add a new empty course with a header (placeholder) at the end
  const addCourse = () => {
    const newId = maxCourseId + 1 || 1;
    setCategories(prev => normalizeCourseSetupState([...prev, {
      id: `placeholder-${newId}`,
      name: "",
      ranking: 0,
      category_group: newId,
      placeholder: true
    }]));
  };
  const addSyncGroup = useCallback(() => {
    const newId = maxSyncGroupId + 1 || 1;
    setSyncGroups(prev => normalizeSyncGroupsState([...prev, {
      groupId: newId,
      stations: []
    }]));
  }, [maxSyncGroupId, normalizeSyncGroupsState]);
  const deleteSyncGroupAndShift = useCallback(groupIdToDelete => {
    setSyncGroups(prev => normalizeSyncGroupsState(prev.filter(group => Number(group.groupId) !== Number(groupIdToDelete)).map(group => ({
      ...group,
      groupId: Number(group.groupId) > Number(groupIdToDelete) ? Number(group.groupId) - 1 : Number(group.groupId)
    }))));
  }, [normalizeSyncGroupsState]);

  // Ensure each course has a header placeholder so the label input is always bound
  const ensureHeaders = useCallback(list => {
    const ids = Array.from(new Set(list.map(c => Number(c.category_group)).filter(n => Number.isFinite(n) && n > 0)));
    const existingHeaderIds = new Set(list.filter(c => c.placeholder).map(c => Number(c.category_group)));
    const toAdd = ids.filter(gid => !existingHeaderIds.has(gid)).map(gid => ({
      id: `placeholder-${gid}`,
      name: "",
      ranking: 0,
      category_group: gid,
      placeholder: true
    }));
    return toAdd.length ? [...list, ...toAdd] : list;
  }, []);
  const normalizeCourseSetupState = useCallback(list => {
    const safeList = Array.isArray(list) ? list.filter(Boolean) : [];
    const assignedGroupIds = Array.from(new Set(safeList.map(item => Number(item?.category_group)).filter(gid => Number.isFinite(gid) && gid > 0))).sort((a, b) => a - b);
    const groupMap = new Map(assignedGroupIds.map((gid, index) => [gid, index + 1]));
    const remapped = safeList.map(item => {
      const gid = Number(item?.category_group);
      if (!Number.isFinite(gid) || gid <= 0 || !groupMap.has(gid)) {
        return {
          ...item,
          category_group: null,
          ranking: 0
        };
      }
      const nextGroup = groupMap.get(gid);
      return {
        ...item,
        category_group: nextGroup,
        id: item?.placeholder ? `placeholder-${nextGroup}` : item.id
      };
    });
    const normalized = [];
    for (const groupId of groupMap.values()) {
      const header = remapped.find(item => item?.placeholder && Number(item?.category_group) === groupId) || {
        id: `placeholder-${groupId}`,
        name: "",
        ranking: 0,
        category_group: groupId,
        placeholder: true
      };
      normalized.push({
        ...header,
        ranking: 0,
        category_group: groupId,
        id: `placeholder-${groupId}`,
        placeholder: true
      });
      const courseItems = remapped.filter(item => !item?.placeholder && Number(item?.category_group) === groupId);
      courseItems.forEach((item, index) => {
        normalized.push({
          ...item,
          category_group: groupId,
          ranking: index + 1
        });
      });
    }
    const unassigned = remapped.filter(item => !item?.placeholder && (!Number.isFinite(Number(item?.category_group)) || Number(item?.category_group) <= 0)).map(item => ({
      ...item,
      category_group: null,
      ranking: 0
    }));
    return [...normalized, ...unassigned];
  }, []);

  // Merge from course A into course B, then delete A (no shifting here by design)
  const mergeCourse = (fromId, toId) => {
    if (fromId === toId) return;
    setCategories(prev => {
      const fromCats = prev.filter(c => Number(c.category_group) === Number(fromId) && !c.placeholder);
      const toCats = prev.filter(c => Number(c.category_group) === Number(toId) && !c.placeholder);
      const toHeader = prev.find(c => c.placeholder && Number(c.category_group) === Number(toId));
      const newToList = [...(toHeader ? [toHeader] : []), ...toCats.sort((a, b) => (a.ranking || 0) - (b.ranking || 0)), ...fromCats].map((c, idx) => ({
        ...c,
        category_group: Number(toId),
        ranking: c.placeholder ? 0 : idx + 1
      }));

      // Remove all rows from source and existing rows of target, then add merged list
      const rest = prev.filter(c => Number(c.category_group) !== Number(fromId) && Number(c.category_group) !== Number(toId));
      return normalizeCourseSetupState(ensureHeaders([...rest, ...newToList]));
    });
  };

  // Delete a course: move its items to unranked, remove the course, then shift all higher courses down by 1
  const deleteCourseAndShift = groupId => {
    setCategories(prev => {
      const toUnassign = prev.filter(c => Number(c.category_group) === Number(groupId) && !c.placeholder);

      // move to unranked (avoid dupes by id)
      setUnrankedCategories(prevUn => {
        const existing = new Set(prevUn.map(u => u.id));
        const add = toUnassign.map(c => ({
          id: c.id,
          name: c.name
        })).filter(x => !existing.has(x.id));
        return [...prevUn, ...add];
      });

      // remove deleted course rows; shift down > groupId
      const withoutDeleted = prev.filter(c => Number(c.category_group) !== Number(groupId));
      const shifted = withoutDeleted.map(c => {
        const gid = Number(c.category_group);
        if (Number.isFinite(gid) && gid > Number(groupId)) {
          const newG = gid - 1;
          return {
            ...c,
            category_group: newG,
            id: c.placeholder ? `placeholder-${newG}` : c.id
          };
        }
        return c;
      });

      // re-number rankings within each remaining course (placeholders rank 0)
      const result = [];
      const byGroup = {};
      shifted.forEach(c => {
        const gid = Number(c.category_group);
        if (Number.isFinite(gid) && gid > 0) {
          if (!byGroup[gid]) byGroup[gid] = [];
          byGroup[gid].push(c);
        } else {
          result.push(c); // unassigned / no group
        }
      });
      Object.keys(byGroup).map(Number).sort((a, b) => a - b).forEach(gid => {
        const rows = byGroup[gid].sort((a, b) => {
          if (a.placeholder && !b.placeholder) return -1;
          if (!a.placeholder && b.placeholder) return 1;
          return (a.ranking || 0) - (b.ranking || 0);
        });
        let rank = 1;
        rows.forEach(row => {
          result.push({
            ...row,
            ranking: row.placeholder ? 0 : rank++
          });
        });
      });
      return normalizeCourseSetupState(ensureHeaders(result));
    });
  };

  // ===== Keybindings & scroll lock for overlay =====
  useEffect(() => {
    if (!isCourseSetupOpen && !isSyncGroupsOpen) return;
    const onKey = e => {
      if (e.key === "Escape") {
        setCourseSetupOpen(false);
        setSyncGroupsOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [isCourseSetupOpen, isSyncGroupsOpen]);
  const fetchJsonWithDebug = useCallback(async (url, label) => {
    const res = await apiFetch(url);
    const raw = await res.text();
    if (!res.ok) {
      console.error(`[TicketHandling] ${label} request failed`, {
        url,
        status: res.status,
        statusText: res.statusText,
        contentType: res.headers.get("content-type"),
        bodyPreview: raw.slice(0, 300)
      });
      throw new Error(`${label} request failed with HTTP ${res.status}`);
    }
    try {
      return JSON.parse(raw);
    } catch (err) {
      console.error(`[TicketHandling] ${label} returned non-JSON`, {
        url,
        status: res.status,
        contentType: res.headers.get("content-type"),
        bodyPreview: raw.slice(0, 300)
      });
      throw err;
    }
  }, []);

  // ===== Load settings and categories =====
  useEffect(() => {
    setLoading(true);
    setCategoriesLoaded(false);
    setSyncGroupsLoaded(false);
    initialRef.current = null;
    savedValuesRef.current = null;
    async function fetchSettings() {
      try {
        // Display
        const uiScaleData = await fetchJsonWithDebug(`/settings/uiscale/${stationId}`, "uiscale");
        setUiScale(uiScaleData.uiscale || uiScaleData.ui_scale || uiScaleData.uiScale || "medium");
        const categoryData = await fetchJsonWithDebug(`/settings/show_category/${stationId}`, "show_category");
        setShowCategory(categoryData.showCategory === true);
        const groupData = await fetchJsonWithDebug(`/settings/group_by_category/${stationId}`, "group_by_category");
        setGroupByCategory(groupData.groupByCategory === true);
        const combineData = await fetchJsonWithDebug(`/settings/combine_condiments/${stationId}`, "combine_condiments");
        setCombineCondiments(combineData.combineCondiments === true);
        const fireTimerData = await fetchJsonWithDebug(`/settings/enable_fire_timer/${stationId}`, "enable_fire_timer");
        setEnableFireTimer(fireTimerData.enableFireTimer === true);
        const resSeparatePreps = await fetchJsonWithDebug(`/settings/separate_itempreps_in_summary/${stationId}`, "separate_itempreps_in_summary");
        setSeparateItemprepsInSummary(resSeparatePreps.separateItemprepsInSummary === true);
        const resHidePreps = await fetchJsonWithDebug(`/settings/hide_preps_in_summary/${stationId}`, "hide_preps_in_summary");
        setHidePrepsInSummary(resHidePreps.hidePrepsInSummary === true);

        // NEW: ticket thresholds
        const resYellow = await fetchJsonWithDebug(`/settings/ticket_color_yellow_after/${stationId}`, "ticket_color_yellow_after");
        setTicketColorYellowAfter(Number(resYellow.ticketColorYellowAfter ?? 5));
        const resRed = await fetchJsonWithDebug(`/settings/ticket_color_red_after/${stationId}`, "ticket_color_red_after");
        setTicketColorRedAfter(Number(resRed.ticketColorRedAfter ?? 10));

        // NEW: course thresholds
        const resCourseYellow = await fetchJsonWithDebug(`/settings/course_color_yellow_after/${stationId}`, "course_color_yellow_after");
        setCourseColorYellowAfter(Number(resCourseYellow.courseColorYellowAfter ?? 7));
        const resCourseRed = await fetchJsonWithDebug(`/settings/course_color_red_after/${stationId}`, "course_color_red_after");
        setCourseColorRedAfter(Number(resCourseRed.courseColorRedAfter ?? 12));

        // Behavior
        const resMerge = await fetchJsonWithDebug(`/settings/merge_mode/${stationId}`, "merge_mode");
        setMergeMode(resMerge.mergeMode || "separate");
        const resEncore = await fetchJsonWithDebug(`/settings/is_encore/${stationId}`, "is_encore");
        {
          const v = resEncore.isEncore;
          setIsEncore(v === true || v === "true");
        }
        const resTicketFirePromotionMode = await fetchJsonWithDebug(`/settings/ticket_fire_promotion_mode/${stationId}`, "ticket_fire_promotion_mode");
        setTicketFirePromotionMode(resTicketFirePromotionMode.ticketFirePromotionMode === "any_fired" || resTicketFirePromotionMode.ticketFirePromotionMode === "all_fired" ? resTicketFirePromotionMode.ticketFirePromotionMode : "none");

        // Coursing
        const resCoursing = await fetchJsonWithDebug(`/settings/coursing/${stationId}`, "coursing");
        setCoursing(resCoursing.coursing === true);
        const resSyncCourses = await fetchJsonWithDebug(`/settings/sync_courses_with_expo/${stationId}`, "sync_courses_with_expo");
        setSyncCoursesWithExpo(resSyncCourses.syncCoursesWithExpo === true);
        const resSyncFiringExpo = await fetchJsonWithDebug(`/settings/sync_firing_with_expo/${stationId}`, "sync_firing_with_expo");
        const syncFiringExpoEnabled = resSyncFiringExpo.syncFiringWithExpo === true;
        setSyncFiringWithExpo(syncFiringExpoEnabled);
        const resSyncFiringAll = await fetchJsonWithDebug(`/settings/sync_firing_with_all_stations/${stationId}`, "sync_firing_with_all_stations");
        const syncFiringAllEnabled = resSyncFiringAll.syncFiringWithAllStations === true;
        setSyncFiringWithAllStations(syncFiringAllEnabled);
        const resFiringSyncMethod = await fetchJsonWithDebug(`/settings/firing_sync_method/${stationId}`, "firing_sync_method");
        setFiringSyncMethod(resFiringSyncMethod.firingSyncMethod || (syncFiringAllEnabled ? "all_stations" : syncFiringExpoEnabled ? "expo" : "none"));
        const resBumpingSyncMethod = await fetchJsonWithDebug(`/settings/bumping_sync_method/${stationId}`, "bumping_sync_method");
        setBumpingSyncMethod(resBumpingSyncMethod.bumpingSyncMethod || "none");
        const resDisplayBumpStatus = await fetchJsonWithDebug(`/settings/display_bump_status_of_other_units/${stationId}`, "display_bump_status_of_other_units");
        setDisplayBumpStatusOfOtherUnits(resDisplayBumpStatus.displayBumpStatusOfOtherUnits === true);
        setDisplayBumpStatusOfOtherUnitsTicket(resDisplayBumpStatus.displayBumpStatusOfOtherUnitsTicket !== false);
        setDisplayBumpStatusOfOtherUnitsCourse(resDisplayBumpStatus.displayBumpStatusOfOtherUnitsCourse === true);
        setDisplayBumpStatusOfOtherUnitsItem(resDisplayBumpStatus.displayBumpStatusOfOtherUnitsItem === true);
        const resCourseLabels = await fetchJsonWithDebug(`/settings/course_labels/${stationId}`, "course_labels");
        courseLabelsRef.current = resCourseLabels?.courseLabels && typeof resCourseLabels.courseLabels === "object" ? resCourseLabels.courseLabels : {};
        const resSyncParkingExpo = await fetchJsonWithDebug(`/settings/sync_parking_with_expo/${stationId}`, "sync_parking_with_expo");
        const syncParkingExpoEnabled = resSyncParkingExpo.syncParkingWithExpo === true;
        setSyncParkingWithExpo(syncParkingExpoEnabled);
        const resSyncParkingAll = await fetchJsonWithDebug(`/settings/sync_parking_with_all_stations/${stationId}`, "sync_parking_with_all_stations");
        const syncParkingAllEnabled = resSyncParkingAll.syncParkingWithAllStations === true;
        setSyncParkingWithAllStations(syncParkingAllEnabled);
        const resParkingSyncMethod = await fetchJsonWithDebug(`/settings/parking_sync_method/${stationId}`, "parking_sync_method");
        setParkingSyncMethod(resParkingSyncMethod.parkingSyncMethod || (syncParkingAllEnabled ? "all_stations" : syncParkingExpoEnabled ? "expo" : "none"));
        const resTicketLocationSyncMethod = await fetchJsonWithDebug(`/settings/ticket_location_sync_method/${stationId}`, "ticket_location_sync_method");
        setTicketLocationSyncMethod(resTicketLocationSyncMethod.ticketLocationSyncMethod || "none");
        const resTicketPageSyncMethod = await fetchJsonWithDebug(`/settings/ticket_page_sync_method/${stationId}`, "ticket_page_sync_method");
        setTicketPageSyncMethod(resTicketPageSyncMethod.ticketPageSyncMethod || "none");
        const resHide = await fetchJsonWithDebug(`/settings/hide_course/${stationId}`, "hide_course");
        setHideCourse(resHide.hideCourse === true);
        const resHideBumped = await fetchJsonWithDebug(`/settings/hide_bumped_courses/${stationId}`, "hide_bumped_courses");
        setHideBumpedCourses(resHideBumped.hideBumpedCourses === true);
        const resDetail = await fetchJsonWithDebug(`/settings/show_detailed_timing_data/${stationId}`, "show_detailed_timing_data");
        setShowDetailedTimingData(resDetail.showDetailedTimingData === true);
        const resColor = await fetchJsonWithDebug(`/settings/ticket_color_by_course/${stationId}`, "ticket_color_by_course");
        setTicketColorByCourse(resColor.ticketColorByCourse === true);
        const resSeparator = await fetchJsonWithDebug(`/settings/use_course_separator/${stationId}`, "use_course_separator");
        setUseCourseSeparator(resSeparator.useCourseSeparator === true);
        const resSeparatorTop = await fetchJsonWithDebug(`/settings/course_separator_top/${stationId}`, "course_separator_top");
        setCourseSeparatorTop(resSeparatorTop.courseSeparatorTop === true);
        const resDisplayCourseLabels = await fetchJsonWithDebug(`/settings/display_course_labels/${stationId}`, "display_course_labels");
        setDisplayCourseLabels(resDisplayCourseLabels.displayCourseLabels === true);
      } catch (err) {
        console.error("Failed to load settings:", err);
      }
    }
    async function fetchCategories() {
      try {
        const data = await fetchJsonWithDebug("/orders/catlist/sorted", "catlist_sorted");

        // Normalize from DB
        let withIds = data.map((cat, idx) => ({
          id: cat.name.toLowerCase().replace(/\s+/g, "-"),
          name: cat.name,
          ranking: cat.ranking || idx + 1,
          category_group: Number.isFinite(Number(cat.category_group)) && Number(cat.category_group) > 0 ? Number(cat.category_group) : null
        }));

        // Ensure every course has a header so label can be edited
        withIds = ensureHeaders(withIds);
        withIds = withIds.map(cat => {
          if (!cat?.placeholder) return cat;
          const label = courseLabelsRef.current?.[String(Number(cat.category_group))];
          return label ? {
            ...cat,
            name: String(label)
          } : cat;
        });
        setCategories(normalizeCourseSetupState(withIds));
      } catch (err) {
        console.error("Failed to load categories:", err);
      } finally {
        setCategoriesLoaded(true);
      }
    }
    async function fetchSyncGroups() {
      try {
        const data = await fetchJsonWithDebug(`/settings/sync_groups_setup/${stationId}`, "sync_groups_setup");
        const stations = (data?.stations || []).map(station => ({
          id: `station-${station.station_id}`,
          station_id: Number(station.station_id),
          name: station.name || `Station ${station.station_id}`,
          role: String(station.role || "").trim().toLowerCase(),
          group_ids: normalizeSyncGroupIds(station.group_ids)
        }));
        setSyncGroupStations(stations);
        setSyncGroups(buildSyncGroupsFromStations(stations));
      } catch (err) {
        console.error("Failed to load sync groups:", err);
        setSyncGroupStations([]);
        setSyncGroups([]);
      } finally {
        setSyncGroupsLoaded(true);
      }
    }
    async function bootstrap() {
      try {
        await Promise.all([fetchSettings(), fetchCategories(), fetchSyncGroups()]);
      } finally {
        setLoading(false);
      }
    }
    bootstrap();
  }, [stationId, ensureHeaders, fetchJsonWithDebug, normalizeCourseSetupState, buildSyncGroupsFromStations, normalizeSyncGroupIds]);
  useEffect(() => {
    let cancelled = false;
    async function fetchStationRole() {
      if (!stationId) {
        setIsExpoStation(false);
        return;
      }
      try {
        const res = await apiFetch(`/updates-reports/stations/by-station-id/${stationId}`);
        if (!res.ok) {
          if (!cancelled) setIsExpoStation(false);
          return;
        }
        const station = await res.json();
        const role = String(station?.role || "").trim().toLowerCase();
        if (!cancelled) {
          setIsExpoStation(role === "expo");
        }
      } catch (err) {
        console.error("Failed to load station role for coursing sync toggle:", err);
        if (!cancelled) setIsExpoStation(false);
      }
    }
    fetchStationRole();
    return () => {
      cancelled = true;
    };
  }, [stationId]);
  useEffect(() => {
    if (loading || !categoriesLoaded || !syncGroupsLoaded || initialRef.current) return;
    const savedValues = buildSavedValues();
    savedValuesRef.current = savedValues;
    initialRef.current = buildSnapshotFromValues(savedValues);
  }, [loading, categoriesLoaded, syncGroupsLoaded, buildSavedValues, buildSnapshotFromValues]);

  // Save all settings + category groups
  const saveToStation = async (targetStationId, shouldUpdateInitial, overrideValues) => {
    setSaving(true);
    try {
      const values = overrideValues || buildSavedValues();
      // Display
      await apiFetch(`/settings/uiscale/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          uiscale: values.uiScale || "medium"
        })
      });
      await apiFetch(`/settings/show_category/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          showCategory: values.showCategory
        })
      });
      await apiFetch(`/settings/group_by_category/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          groupByCategory: values.groupByCategory
        })
      });
      await apiFetch(`/settings/combine_condiments/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          combineCondiments: values.combineCondiments
        })
      });
      await apiFetch(`/settings/enable_fire_timer/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          enableFireTimer: values.enableFireTimer
        })
      });
      await apiFetch(`/settings/separate_itempreps_in_summary/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          separateItemprepsInSummary: values.summaryMode === "separate"
        })
      });
      await apiFetch(`/settings/hide_preps_in_summary/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          hidePrepsInSummary: values.summaryMode === "hidden"
        })
      });

      // NEW: ticket thresholds
      await apiFetch(`/settings/ticket_color_yellow_after/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ticketColorYellowAfter: Number(values.ticketColorYellowAfter)
        })
      });
      await apiFetch(`/settings/ticket_color_red_after/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ticketColorRedAfter: Number(values.ticketColorRedAfter)
        })
      });

      // NEW: course thresholds
      await apiFetch(`/settings/course_color_yellow_after/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          courseColorYellowAfter: Number(values.courseColorYellowAfter)
        })
      });
      await apiFetch(`/settings/course_color_red_after/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          courseColorRedAfter: Number(values.courseColorRedAfter)
        })
      });

      // Behavior
      await apiFetch(`/settings/merge_mode/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          mergeMode: values.mergeMode
        })
      });
      await apiFetch(`/settings/is_encore/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          isEncore: values.isEncore
        })
      });
      await apiFetch(`/settings/ticket_fire_promotion_mode/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ticketFirePromotionMode: values.ticketFirePromotionMode
        })
      });

      // Coursing
      await apiFetch(`/settings/coursing/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          coursing: values.coursing
        })
      });
      await apiFetch(`/settings/sync_courses_with_expo/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          syncCoursesWithExpo: values.syncCoursesWithExpo
        })
      });
      await apiFetch(`/settings/sync_firing_with_expo/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          syncFiringWithExpo: values.firingSyncMethod === "expo" || !!values.syncFiringWithExpo && values.firingSyncMethod !== "all_stations"
        })
      });
      await apiFetch(`/settings/sync_firing_with_all_stations/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          syncFiringWithAllStations: values.firingSyncMethod === "all_stations"
        })
      });
      await apiFetch(`/settings/firing_sync_method/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          firingSyncMethod: values.firingSyncMethod
        })
      });
      await apiFetch(`/settings/bumping_sync_method/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          bumpingSyncMethod: values.bumpingSyncMethod
        })
      });
      await apiFetch(`/settings/display_bump_status_of_other_units/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          displayBumpStatusOfOtherUnits: values.displayBumpStatusOfOtherUnits,
          displayBumpStatusOfOtherUnitsTicket: values.displayBumpStatusOfOtherUnitsTicket,
          displayBumpStatusOfOtherUnitsCourse: values.displayBumpStatusOfOtherUnitsCourse,
          displayBumpStatusOfOtherUnitsItem: values.displayBumpStatusOfOtherUnitsItem
        })
      });
      await apiFetch(`/settings/sync_parking_with_expo/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          syncParkingWithExpo: values.parkingSyncMethod === "expo" || !!values.syncParkingWithExpo && values.parkingSyncMethod !== "all_stations"
        })
      });
      await apiFetch(`/settings/sync_parking_with_all_stations/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          syncParkingWithAllStations: values.parkingSyncMethod === "all_stations"
        })
      });
      await apiFetch(`/settings/parking_sync_method/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          parkingSyncMethod: values.parkingSyncMethod
        })
      });
      await apiFetch(`/settings/ticket_location_sync_method/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ticketLocationSyncMethod: values.ticketLocationSyncMethod
        })
      });
      await apiFetch(`/settings/ticket_page_sync_method/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ticketPageSyncMethod: values.ticketPageSyncMethod
        })
      });
      await apiFetch(`/settings/sync_groups_setup/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          stationGroups: values.syncGroups || {}
        })
      });
      await apiFetch(`/settings/hide_course/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          hideCourse: values.hideCourse
        })
      });
      await apiFetch(`/settings/hide_bumped_courses/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          hideBumpedCourses: values.hideBumpedCourses
        })
      });
      await apiFetch(`/settings/show_detailed_timing_data/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          showDetailedTimingData: values.showDetailedTimingData
        })
      });
      await apiFetch(`/settings/ticket_color_by_course/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ticketColorByCourse: values.ticketColorByCourse
        })
      });
      await apiFetch(`/settings/use_course_separator/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          useCourseSeparator: values.useCourseSeparator
        })
      });
      await apiFetch(`/settings/course_separator_top/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          courseSeparatorTop: values.courseSeparatorTop
        })
      });
      await apiFetch(`/settings/display_course_labels/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          displayCourseLabels: values.displayCourseLabels
        })
      });
      await apiFetch(`/settings/course_labels/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          courseLabels: values.courseLabels
        })
      });

      // Persist categories (exclude placeholders)
      const grouped = (values.categories || []).filter(c => c && c.name).map(c => ({
        name: c.name,
        ranking: c.ranking,
        category_group: c.category_group
      }));
      await apiFetch(`/orders/catlist/save`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          categories: grouped
        })
      });
      if (shouldUpdateInitial) {
        savedValuesRef.current = values;
        initialRef.current = buildSnapshotFromValues(values);
      }
    } catch (err) {
      console.error("Failed to save settings:", err);
      throw err;
    } finally {
      setSaving(false);
    }
  };
  const getChanges = useCallback(() => {
    if (!initialRef.current) return [];
    const initial = initialRef.current;
    const current = buildSnapshot();
    const changes = [];
    const boolLabel = v => v ? "On" : "Off";
    const add = (label, from, to) => {
      if (from !== to) {
        changes.push(`${label}: ${from} -> ${to}`);
      }
    };
    add("UI scale", initial.uiScale, current.uiScale);
    add("Show item categories", boolLabel(initial.showCategory), boolLabel(current.showCategory));
    add("Group items by category", boolLabel(initial.groupByCategory), boolLabel(current.groupByCategory));
    add("Combine condiments", boolLabel(initial.combineCondiments), boolLabel(current.combineCondiments));
    add("Enable fire timer", boolLabel(initial.enableFireTimer), boolLabel(current.enableFireTimer));
    add("Summary display", initial.summaryMode, current.summaryMode);
    add("Ticket color yellow after (min)", initial.ticketColorYellowAfter, current.ticketColorYellowAfter);
    add("Ticket color red after (min)", initial.ticketColorRedAfter, current.ticketColorRedAfter);
    add("Course color yellow after (min)", initial.courseColorYellowAfter, current.courseColorYellowAfter);
    add("Course color red after (min)", initial.courseColorRedAfter, current.courseColorRedAfter);
    add("Merge mode", initial.mergeMode, current.mergeMode);
    add("Encore mode", boolLabel(initial.isEncore), boolLabel(current.isEncore));
    add("Ticket fire promotion", initial.ticketFirePromotionMode, current.ticketFirePromotionMode);
    add("Coursing", boolLabel(initial.coursing), boolLabel(current.coursing));
    add("Sync courses with expo", boolLabel(initial.syncCoursesWithExpo), boolLabel(current.syncCoursesWithExpo));
    add("Firing sync method", initial.firingSyncMethod, current.firingSyncMethod);
    add("Bumping sync method", initial.bumpingSyncMethod, current.bumpingSyncMethod);
    add("Display bump status of other units", boolLabel(initial.displayBumpStatusOfOtherUnits), boolLabel(current.displayBumpStatusOfOtherUnits));
    add("Display other units on ticket level", boolLabel(initial.displayBumpStatusOfOtherUnitsTicket), boolLabel(current.displayBumpStatusOfOtherUnitsTicket));
    add("Display other units on course level", boolLabel(initial.displayBumpStatusOfOtherUnitsCourse), boolLabel(current.displayBumpStatusOfOtherUnitsCourse));
    add("Display other units on item level", boolLabel(initial.displayBumpStatusOfOtherUnitsItem), boolLabel(current.displayBumpStatusOfOtherUnitsItem));
    add("Parking sync method", initial.parkingSyncMethod, current.parkingSyncMethod);
    add("Ticket location sync method", initial.ticketLocationSyncMethod, current.ticketLocationSyncMethod);
    add("Ticket page sync method", initial.ticketPageSyncMethod, current.ticketPageSyncMethod);
    add("Hide next course", boolLabel(initial.hideCourse), boolLabel(current.hideCourse));
    add("Hide bumped courses", boolLabel(initial.hideBumpedCourses), boolLabel(current.hideBumpedCourses));
    add("Show detailed timing", boolLabel(initial.showDetailedTimingData), boolLabel(current.showDetailedTimingData));
    add("Ticket color by course", boolLabel(initial.ticketColorByCourse), boolLabel(current.ticketColorByCourse));
    add("Use course separator", boolLabel(initial.useCourseSeparator), boolLabel(current.useCourseSeparator));
    add("Course separator on top", boolLabel(initial.courseSeparatorTop), boolLabel(current.courseSeparatorTop));
    add("Display course labels", boolLabel(initial.displayCourseLabels), boolLabel(current.displayCourseLabels));
    if (initial.syncGroupsHash !== current.syncGroupsHash) {
      changes.push("Sync groups updated");
    }
    if (initial.courseLabelsHash !== current.courseLabelsHash) {
      changes.push("Course labels updated");
    }
    if (initial.categoriesHash !== current.categoriesHash) {
      changes.push("Course setup / category ordering updated");
    }
    return changes;
  }, [buildSnapshot, stationId]);
  useSettingsSection("ticket-handling", useMemo(() => ({
    getChanges,
    save: () => saveToStation(stationId, true),
    applyToStation: targetStationId => saveToStation(targetStationId, false),
    applySavedToStation: targetStationId => {
      if (!savedValuesRef.current) return Promise.resolve();
      return saveToStation(targetStationId, false, savedValuesRef.current);
    }
  }), [getChanges, stationId, categories, syncGroups, uiScale, showCategory, groupByCategory, combineCondiments, enableFireTimer, separateItemprepsInSummary, hidePrepsInSummary, ticketColorYellowAfter, ticketColorRedAfter, courseColorYellowAfter, courseColorRedAfter, mergeMode, isEncore, ticketFirePromotionMode, coursing, syncCoursesWithExpo, firingSyncMethod, syncFiringWithExpo, syncFiringWithAllStations, bumpingSyncMethod, displayBumpStatusOfOtherUnits, displayBumpStatusOfOtherUnitsTicket, displayBumpStatusOfOtherUnitsCourse, displayBumpStatusOfOtherUnitsItem, parkingSyncMethod, syncParkingWithExpo, syncParkingWithAllStations, ticketLocationSyncMethod, ticketPageSyncMethod, hideCourse, hideBumpedCourses, showDetailedTimingData, ticketColorByCourse, useCourseSeparator, courseSeparatorTop, displayCourseLabels]));

  // Load unranked categories when opening "categories" tab
  const loadUnrankedCategories = useCallback(async () => {
    try {
      const res = await apiFetch("/orders/catlist/from-items");
      const itemsData = await res.json();
      const assignedNames = new Set(categories.filter(c => Number.isFinite(Number(c?.category_group)) && Number(c.category_group) > 0 && !c.placeholder).map(c => normalizeCategoryName(c.name)));
      const unranked = itemsData.filter(item => !assignedNames.has(normalizeCategoryName(item.name)));
      const withIds = unranked.map(cat => ({
        id: `unranked-${cat.name.toLowerCase().replace(/\s+/g, "-")}`,
        name: cat.name
      }));
      setUnrankedCategories(withIds);
    } catch (err) {
      console.error("Failed to load unranked categories:", err);
    }
  }, [categories, normalizeCategoryName]);
  useEffect(() => {
    if (subTab === "categories" && categoriesLoaded) {
      loadUnrankedCategories();
    }
  }, [subTab, categoriesLoaded, categories, loadUnrankedCategories]);
  useEffect(() => {
    if (forcedSubTab) {
      setSubTab(forcedSubTab);
    }
  }, [forcedSubTab]);
  const showCoursingBehaviorGroup = !sectionMode || sectionMode === "coursing";
  const showSyncStatusGroup = !sectionMode || sectionMode === "syncStatus";
  return <div className="mx-auto max-w-4xl rounded-2xl bg-white p-6 shadow dark:bg-gray-800">
      {!hideSubTabBar && <div className="flex gap-4 mb-4 border-b border-gray-300 dark:border-gray-700">
          <button onClick={() => setSubTab("display")} className={`pb-2 font-medium text-sm ${subTab === "display" ? "border-b-2 border-blue-500 text-blue-600 dark:text-blue-400" : "text-gray-600 dark:text-gray-400"}`}>
            Display Settings
          </button>
          <button onClick={() => setSubTab("behavior")} className={`pb-2 font-medium text-sm ${subTab === "behavior" ? "border-b-2 border-blue-500 text-blue-600 dark:text-blue-400" : "text-gray-600 dark:text-gray-400"}`}>
            Ticket Behavior
          </button>
          <button onClick={() => setSubTab("coursing")} className={`pb-2 font-medium text-sm ${subTab === "coursing" ? "border-b-2 border-blue-500 text-blue-600 dark:text-blue-400" : "text-gray-600 dark:text-gray-400"}`}>
            Coursing Settings
          </button>
          <button onClick={() => setSubTab("categories")} className={`pb-2 font-medium text-sm ${subTab === "categories" ? "border-b-2 border-blue-500 text-blue-600 dark:text-blue-400" : "text-gray-600 dark:text-gray-400"}`}>
            Courses Setup
          </button>
        </div>}

      {loading ? <SettingsSectionSkeleton titleWidth="w-40" rows={5} /> : <>
          {/* Display Settings */}
          {subTab === "display" && <div className="space-y-4">
              <div className="text-center">
                <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
                  Appearance
                </h2>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Tune how tickets look and how timing information is shown.
                </p>
              </div>

              <SelectCard label="UI Scale" description="Control the overall ticket UI scale for order numbers, items, timers, and course headers." value={uiScale} onChange={e => setUiScale(e.target.value)} options={[{
          value: "small",
          label: "Small (compact)"
        }, {
          value: "medium",
          label: "Medium (default)"
        }, {
          value: "large",
          label: "Large (easy to read)"
        }, {
          value: "extralarge",
          label: "Extra Large (very easy to read)"
        }]} />

              <ToggleCard label="Show Item Category On Ticket Items" description="Show the category label alongside each item on the ticket." checked={showCategory} onToggle={() => setShowCategory(prev => !prev)} />

              <ToggleCard label="Show Items Grouped Within Category" description="Keep items organized by category on the ticket." checked={groupByCategory} onToggle={() => setGroupByCategory(prev => !prev)} />

              <ToggleCard label='Combine Modifiers Like "No" And "On The Side"' description='Combine prep phrases like "No" or "Extra" into a single readable modifier line.' checked={combineCondiments} onToggle={() => setCombineCondiments(prev => !prev)} />

              <ToggleCard label="Enable Ticket Fire Timer" description="Show how long a ticket has been fired." checked={enableFireTimer} onToggle={() => setEnableFireTimer(prev => !prev)} />

              <ToggleCard label="Show Detailed Timing Info" description="Show extra timing data such as received, fired, bumped, and duration details." checked={showDetailedTimingData} onToggle={() => setShowDetailedTimingData(prev => !prev)} />

              <SelectCard label="Summary Display" description="Choose how the summary combines prep options." value={hidePrepsInSummary ? "hidden" : separateItemprepsInSummary ? "separate" : "combined"} onChange={e => {
          const value = e.target.value;
          setSeparateItemprepsInSummary(value === "separate");
          setHidePrepsInSummary(value === "hidden");
        }} options={[{
          value: "combined",
          label: "Fully Combined (Default)"
        }, {
          value: "separate",
          label: "Separate Prep Options"
        }, {
          value: "hidden",
          label: "Hide Prep Options"
        }]} />

              <ThresholdCard label="Ticket Color Thresholds" description="Set when ticket backgrounds change from green to yellow to red." firstLabel="Yellow After (minutes)" firstValue={ticketColorYellowAfter} onFirstChange={e => setTicketColorYellowAfter(e.target.value)} secondLabel="Red After (minutes)" secondValue={ticketColorRedAfter} onSecondChange={e => setTicketColorRedAfter(e.target.value)} warning={Number(ticketColorRedAfter) > 0 && Number(ticketColorYellowAfter) > 0 && Number(ticketColorRedAfter) < Number(ticketColorYellowAfter) ? "Red should usually be greater than Yellow. We'll still handle it safely, but you may want to swap them." : ""} />
            </div>}

          {/* Behavior Settings */}
          {subTab === "behavior" && <div className="space-y-4">
              <div className="text-center">
                <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
                  Ticket Flow
                </h2>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Control how tickets behave as they arrive and merge.
                </p>
              </div>

              <SelectCard label="When to Move Tickets Forward After Firing" description="Choose when a ticket should move to the front after items are fired." value={ticketFirePromotionMode} onChange={e => setTicketFirePromotionMode(e.target.value)} options={[{
          value: "none",
          label: "Never move fired tickets"
        }, {
          value: "any_fired",
          label: "Move when any item is fired"
        }, {
          value: "all_fired",
          label: "Move only when all items are fired"
        }]} />

              <SelectCard label="Ticket Merge Behavior" description="Decide how to handle new tickets that belong to the same order." value={mergeMode} onChange={e => setMergeMode(e.target.value)} options={[{
          value: "separate",
          label: "Always Create New Ticket"
        }, {
          value: "merge",
          label: "Merge with Existing (if pending)"
        }]} />

              <ToggleCard label="Enable Encore-Specific Behavior" description="Turn on Encore-specific ticket logic." checked={isEncore} onToggle={() => setIsEncore(prev => !prev)} />
            </div>}

          {/* Coursing Settings */}
          {subTab === "coursing" && <div className="space-y-6">
              {showCoursingBehaviorGroup && <div className="space-y-4">
                  <div className="text-center">
                    <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
                      Coursing
                    </h2>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      Control course behavior, visibility, and course-based timing.
                    </p>
                  </div>

                  <ToggleCard label="Enable Coursing" description="Enable course controls so a course can fire or bump all of its items together." checked={coursing} onToggle={() => setCoursing(prev => !prev)} />

                  <ToggleCard label="Hide Next Course" description="Keep the next course hidden until the previous course has been bumped." checked={hideCourse} onToggle={() => setHideCourse(prev => !prev)} />

                  <ToggleCard label="Hide Bumped Courses" description="Hide a course after all of its bumped items are complete." checked={hideBumpedCourses} onToggle={() => setHideBumpedCourses(prev => !prev)} />

                  <ToggleCard label="Ticket Color By Course" description="Use the current course timer instead of full ticket age for ticket color thresholds." checked={ticketColorByCourse} onToggle={() => setTicketColorByCourse(prev => !prev)} />

                  <ThresholdCard label="Course Color Thresholds" description='Set when course timers change from green to yellow to red. These thresholds also power "Ticket Color By Course".' firstLabel="Yellow After (minutes)" firstValue={courseColorYellowAfter} onFirstChange={e => setCourseColorYellowAfter(e.target.value)} secondLabel="Red After (minutes)" secondValue={courseColorRedAfter} onSecondChange={e => setCourseColorRedAfter(e.target.value)} warning={Number(courseColorRedAfter) > 0 && Number(courseColorYellowAfter) > 0 && Number(courseColorRedAfter) < Number(courseColorYellowAfter) ? "Red should usually be greater than Yellow. We'll still handle it safely, but you may want to swap them." : ""} />
                </div>}

              {showSyncStatusGroup && <div className="space-y-4">
                  <div className="text-center">
                    <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
                      Sync & Status
                    </h2>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      Decide what stays local, what follows Expo, and what syncs across stations.
                    </p>
                  </div>

                  <ToggleCard label="Sync Courses With Expo" description={isExpoStation ? "This setting can be changed here, but it has no effect while this screen is Expo." : "Keep station course progression aligned with Expo coursing."} checked={syncCoursesWithExpo} onToggle={() => setSyncCoursesWithExpo(prev => !prev)} />

                  <SelectCard label="Firing Sync Method" description="Choose whether item, course, and ticket fires stay local, follow Expo, or sync across all screens." value={firingSyncMethod} onChange={e => {
            const next = e.target.value;
            setFiringSyncMethod(next);
            setSyncFiringWithExpo(next === "expo");
            setSyncFiringWithAllStations(next === "all_stations");
          }} options={[{
            value: "none",
            label: "No Sync"
          }, {
            value: "expo",
            label: "Sync with Expo"
          }, {
            value: "sync_group",
            label: "Sync Group Only"
          }, {
            value: "all_stations",
            label: "Sync with All Stations"
          }]} />

                  <SelectCard label="Bumping Sync Method" description="Choose whether bump events stay local, follow Expo, or sync across all screens." value={bumpingSyncMethod} onChange={e => setBumpingSyncMethod(e.target.value)} options={[{
            value: "none",
            label: "No Sync"
          }, {
            value: "expo",
            label: "Sync with Expo"
          }, {
            value: "sync_group",
            label: "Sync Group Only"
          }, {
            value: "all_stations",
            label: "Sync with All Stations"
          }]} />

                  <ToggleCard label="Display Bump Status Of Other Units" description="Show status boxes from other units without changing local bumped state." checked={displayBumpStatusOfOtherUnits} onToggle={() => setDisplayBumpStatusOfOtherUnits(prev => !prev)} />

                  <div className="grid gap-3 sm:grid-cols-3">
                    {[{
              key: "ticket",
              label: "Ticket Level",
              description: "Show other-unit bump status on tickets.",
              checked: displayBumpStatusOfOtherUnitsTicket,
              setChecked: setDisplayBumpStatusOfOtherUnitsTicket
            }, {
              key: "course",
              label: "Course Level",
              description: "Show other-unit bump status on courses.",
              checked: displayBumpStatusOfOtherUnitsCourse,
              setChecked: setDisplayBumpStatusOfOtherUnitsCourse
            }, {
              key: "item",
              label: "Item Level",
              description: "Show other-unit bump status on items.",
              checked: displayBumpStatusOfOtherUnitsItem,
              setChecked: setDisplayBumpStatusOfOtherUnitsItem
            }].map(option => <ToggleCard key={option.key} label={option.label} description={option.description} checked={option.checked} onToggle={() => option.setChecked(prev => !prev)} disabled={!displayBumpStatusOfOtherUnits} />)}
                  </div>

                  <SelectCard label="Parking Sync Method" description="Choose whether parking changes stay local, follow Expo, or sync across all screens." value={parkingSyncMethod} onChange={e => {
            const next = e.target.value;
            setParkingSyncMethod(next);
            setSyncParkingWithExpo(next === "expo");
            setSyncParkingWithAllStations(next === "all_stations");
          }} options={[{
            value: "none",
            label: "No Sync"
          }, {
            value: "expo",
            label: "Sync with Expo"
          }, {
            value: "sync_group",
            label: "Sync Group Only"
          }, {
            value: "all_stations",
            label: "Sync with All Stations"
          }]} />

                  <SelectCard label="Ticket Location Sync Method" description="Choose whether ticket reordering stays local, follows Expo, or syncs across all screens." value={ticketLocationSyncMethod} onChange={e => setTicketLocationSyncMethod(e.target.value)} options={[{
            value: "none",
            label: "No Sync"
          }, {
            value: "expo",
            label: "Sync with Expo"
          }, {
            value: "sync_group",
            label: "Sync Group Only"
          }, {
            value: "all_stations",
            label: "Sync with All Stations"
          }]} />

                  <SelectCard label="Ticket Page Sync Method" description="Choose whether page changes stay local, follow Expo, or sync across all screens." value={ticketPageSyncMethod} onChange={e => setTicketPageSyncMethod(e.target.value)} options={[{
            value: "none",
            label: "No Sync"
          }, {
            value: "expo",
            label: "Sync with Expo"
          }, {
            value: "sync_group",
            label: "Sync Group Only"
          }, {
            value: "all_stations",
            label: "Sync with All Stations"
          }]} />

                  <div className="flex justify-center">
                    <button type="button" onClick={() => setSyncGroupsOpen(true)} className="rounded-xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white hover:bg-indigo-700">
                      Setup Sync Groups
                    </button>
                  </div>
                </div>}
            </div>}

          {/* Courses Setup */}
          {subTab === "categories" && <div className="mb-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Mode
                </label>
                <div className="inline-flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                  <button type="button" onClick={() => setUseCourseSeparator(true)} className={`px-3 py-2 text-sm font-medium transition ${useCourseSeparator ? "bg-green-600 text-white" : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"}`} aria-pressed={useCourseSeparator}>
                    Course Separator
                  </button>
                  <button type="button" onClick={() => setUseCourseSeparator(false)} className={`px-3 py-2 text-sm font-medium transition border-l border-gray-300 dark:border-gray-600 ${!useCourseSeparator ? "bg-indigo-600 text-white" : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"}`} aria-pressed={!useCourseSeparator}>
                    Course Assignment
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Choose how courses are determined for tickets.
                </p>
              </div>

              <fieldset disabled={!useCourseSeparator} className={`${useCourseSeparator ? "" : "opacity-50 pointer-events-none select-none"}`}>
                <legend className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">
                  Course Separator
                </legend>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={courseSeparatorTop} onChange={e => setCourseSeparatorTop(e.target.checked)} className="w-4 h-4" />
                  <span>Place separator at the top of each course</span>
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  When enabled, the separator banner renders above course items instead of below.
                </p>
              </fieldset>

              <fieldset disabled={useCourseSeparator} className={`${useCourseSeparator ? "opacity-50 pointer-events-none select-none" : ""}`}>
                <legend className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">
                  Course Assignment
                </legend>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Manage courses and drag categories in a full-screen workspace.
                </p>
                <div className="mb-3">
                  <ToggleCard label="Display Course Labels" description="Show the course number by default and replace it with an optional course label when one exists." checked={displayCourseLabels} onToggle={() => setDisplayCourseLabels(prev => !prev)} disabled={useCourseSeparator} />
                </div>
                <button type="button" onClick={() => setCourseSetupOpen(true)} disabled={useCourseSeparator} className="text-sm px-3 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
                  Setup Courses
                </button>
              </fieldset>
            </div>}

        </>}

      {/* ===== Full-screen Course Setup Overlay ===== */}
      {isCourseSetupOpen && <div className="fixed inset-0 z-[100]" aria-modal="true" role="dialog">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCourseSetupOpen(false)} />

          <div className="absolute inset-0 bg-white dark:bg-gray-900 flex flex-col">
            <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-800">
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                  Course Setup
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Drag categories into courses. Items at the top show first on tickets.
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button className="text-xs px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600" onClick={() => setCourseSetupOpen(false)}>
                  Close (Esc)
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-hidden p-4">
              <div className="max-w-6xl mx-auto h-full grid grid-cols-1 lg:grid-cols-12 gap-4">
                {/* Left pane */}
                <div className="lg:col-span-5 flex flex-col min-h-0">
                  <div className="sticky top-0 z-10 bg-white/80 dark:bg-gray-900/80 backdrop-blur border-b border-gray-200 dark:border-gray-800">
                    <div className="flex items-center justify-between py-2">
                      <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                        Categories w/o Course
                      </h3>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {unrankedCategories.length} unassigned
                      </span>
                    </div>
                  </div>

                  <div className="flex-1 overflow-auto pt-2">
                    <ReactSortable list={unrankedCategories} setList={newList => {
                  setUnrankedCategories(newList);
                  setCategories(prev => normalizeCourseSetupState(prev.map(c => newList.some(u => u.id === c.id) ? {
                    ...c,
                    category_group: null,
                    ranking: 0
                  } : c)));
                }} animation={200} group="categories" delayOnTouchOnly={true} className="grid grid-cols-2 gap-2 min-h-[6rem] border border-dashed border-gray-400 dark:border-gray-600 rounded p-2 bg-yellow-50/60 dark:bg-yellow-900/30">
                      {unrankedCategories.length === 0 ? <div className="col-span-2 text-sm italic text-gray-400 dark:text-gray-500">
                          Drag categories here to unassign
                        </div> : unrankedCategories.map(cat => <div key={cat.id} data-id={cat.id} className="bg-yellow-100 dark:bg-yellow-900 text-yellow-900 dark:text-yellow-100 p-2 rounded border border-yellow-300 dark:border-yellow-700">
                            {cat.name}
                          </div>)}
                    </ReactSortable>
                  </div>
                </div>

                {/* Right pane */}
                <div className="lg:col-span-7 flex flex-col min-h-0">
                  <div className="sticky top-0 z-10 bg-white/80 dark:bg-gray-900/80 backdrop-blur border-b border-gray-200 dark:border-gray-800">
                    <div className="flex items-center justify-between py-2">
                      <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                        Courses
                      </h3>
                      <button onClick={addCourse} className="text-xs px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600">
                        Add Course
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-auto pt-2 space-y-4 pr-1">
                    {getExistingCourseIds().map(groupId => {
                  const groupCats = categories.filter(c => Number(c.category_group) === Number(groupId)).sort((a, b) => {
                    if (a.placeholder && !b.placeholder) return -1;
                    if (!a.placeholder && b.placeholder) return 1;
                    return (a.ranking || 0) - (b.ranking || 0);
                  });
                  const sortableCourseItems = groupCats.filter(c => !c.placeholder);
                  const header = groupCats.find(c => c.placeholder);
                  const allIds = getExistingCourseIds();
                  const otherIds = allIds.filter(id => id !== Number(groupId));
                  const hasNext = allIds.includes(Number(groupId) + 1);
                  return <div key={groupId} className="rounded-lg border border-gray-300 dark:border-gray-700">
                          <div className="flex items-center justify-between px-3 py-2 bg-purple-50 dark:bg-purple-950 rounded-t-lg">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs uppercase font-semibold text-purple-600 dark:text-purple-300">
                                Course {groupId}
                              </span>
                              <input type="text" value={header ? header.name : ""} onChange={e => {
                          const val = e.target.value;
                          setCategories(prev => normalizeCourseSetupState(prev.map(c => c.placeholder && Number(c.category_group) === Number(groupId) ? {
                            ...c,
                            name: val
                          } : c)));
                        }} placeholder="Optional course label" className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800" />
                            </div>
                            <div className="flex items-center gap-1 flex-wrap">
                              {hasNext && <button onClick={() => mergeCourse(Number(groupId) + 1, Number(groupId))} className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-200" title={`Move all items from Course ${Number(groupId) + 1} into Course ${groupId}`}>
                                  Pull From Next
                                </button>}
                              {otherIds.length > 0 && <select onChange={e => {
                          const from = Number(e.target.value);
                          if (from) mergeCourse(from, Number(groupId));
                          e.target.selectedIndex = 0;
                        }} className="text-xs px-1 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800" defaultValue="">
                                  <option value="" disabled>
                                    Merge Fromâ€¦
                                  </option>
                                  {otherIds.map(id => <option key={id} value={id}>
                                      Course {id}
                                    </option>)}
                                </select>}
                              <button onClick={() => deleteCourseAndShift(Number(groupId))} className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/40 dark:text-red-200" aria-label={`Delete Course ${groupId}`}>
                                Delete
                              </button>
                            </div>
                          </div>

                          <ReactSortable list={sortableCourseItems} setList={newList => {
                      setCategories(prev => {
                        const headerRow = prev.find(c => c.placeholder && Number(c.category_group) === Number(groupId));
                        const others = prev.filter(c => Number(c.category_group) !== Number(groupId));
                        const updated = [...(headerRow ? [headerRow] : []), ...newList.map((c, idx) => ({
                          ...c,
                          ranking: idx + 1,
                          category_group: Number(groupId)
                        }))];
                        return normalizeCourseSetupState([...others, ...updated]);
                      });
                    }} group="categories" animation={200} delayOnTouchOnly={true} className="space-y-2 min-h-[3rem] p-3">
                            {sortableCourseItems.length === 0 ? <div className="text-sm italic text-gray-400 dark:text-gray-500">
                                Drop categories here
                              </div> : sortableCourseItems.sort((a, b) => (a.ranking || 0) - (b.ranking || 0)).map(cat => <div key={cat.id} data-id={cat.id} className="bg-white dark:bg-gray-800 p-2 rounded border border-gray-300 dark:border-gray-600">
                                    {cat.name}
                                  </div>)}
                          </ReactSortable>
                        </div>;
                })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
      {/* ===== End Full-screen Course Setup Overlay ===== */}

      {isSyncGroupsOpen && <div className="fixed inset-0 z-[100]" aria-modal="true" role="dialog">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSyncGroupsOpen(false)} />

          <div className="absolute inset-0 bg-white dark:bg-gray-900 flex flex-col">
            <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-800">
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                  Sync Groups
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Drag copies of stations into groups. A station can belong to multiple groups.
                </span>
              </div>
              <button className="text-xs px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600" onClick={() => setSyncGroupsOpen(false)}>
                Close (Esc)
              </button>
            </div>

            <div className="flex-1 overflow-hidden p-4">
              <div className="max-w-6xl mx-auto h-full grid grid-cols-1 lg:grid-cols-12 gap-4">
                <div className="lg:col-span-5 flex flex-col min-h-0">
                  <div className="sticky top-0 z-10 bg-white/80 dark:bg-gray-900/80 backdrop-blur border-b border-gray-200 dark:border-gray-800">
                    <div className="flex items-center justify-between py-2">
                      <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                        Stations
                      </h3>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {syncGroupStations.length} available to copy
                      </span>
                    </div>
                  </div>

                  <div className="flex-1 overflow-auto pt-2">
                    <ReactSortable list={syncGroupStations} setList={() => {}} sort={false} animation={200} delayOnTouchOnly={true} group={{
                  name: "sync-groups",
                  pull: "clone",
                  put: true
                }} clone={station => buildSyncGroupClone(station, 0)} className="grid grid-cols-1 gap-2 min-h-[6rem] border border-dashed border-gray-400 dark:border-gray-600 rounded p-2 bg-blue-50/60 dark:bg-blue-950/20">
                      {syncGroupStations.map(station => <div key={station.id} data-id={station.id} className="flex items-center justify-between rounded border border-blue-200 bg-white px-3 py-2 dark:border-blue-800 dark:bg-gray-800">
                          <span className="text-sm text-gray-800 dark:text-gray-100">
                            {station.name}
                          </span>
                          <span className="text-xs uppercase text-gray-500 dark:text-gray-400">
                            {station.role || "station"} #{station.station_id}
                          </span>
                        </div>)}
                    </ReactSortable>
                  </div>
                </div>

                <div className="lg:col-span-7 flex flex-col min-h-0">
                  <div className="sticky top-0 z-10 bg-white/80 dark:bg-gray-900/80 backdrop-blur border-b border-gray-200 dark:border-gray-800">
                    <div className="flex items-center justify-between py-2">
                      <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                        Sync Groups
                      </h3>
                      <button onClick={addSyncGroup} className="text-xs px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600">
                        Add Group
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-auto pt-2 space-y-4 pr-1">
                    {syncGroups.length === 0 ? <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                        No sync groups yet. Add a group to start assigning stations.
                      </div> : syncGroups.map(group => <div key={group.groupId} className="rounded-lg border border-gray-300 dark:border-gray-700">
                          <div className="flex items-center justify-between px-3 py-2 bg-indigo-50 dark:bg-indigo-950 rounded-t-lg">
                            <span className="text-xs uppercase font-semibold text-indigo-600 dark:text-indigo-300">
                              Group {group.groupId}
                            </span>
                            <button onClick={() => deleteSyncGroupAndShift(group.groupId)} className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/40 dark:text-red-200">
                              Delete
                            </button>
                          </div>

                          <ReactSortable list={group.stations} setList={newList => {
                    setSyncGroups(prev => normalizeSyncGroupsState(prev.map(entry => {
                      if (Number(entry.groupId) !== Number(group.groupId)) return entry;
                      const deduped = [];
                      const seen = new Set();
                      newList.forEach(station => {
                        const stationId = Number(station?.station_id);
                        if (!Number.isFinite(stationId) || seen.has(stationId)) return;
                        seen.add(stationId);
                        deduped.push(buildSyncGroupClone(station, group.groupId));
                      });
                      return {
                        ...entry,
                        stations: deduped
                      };
                    })));
                  }} group={{
                    name: "sync-groups",
                    pull: true,
                    put: true
                  }} animation={200} delayOnTouchOnly={true} className="space-y-2 min-h-[3rem] p-3">
                            {group.stations.length === 0 ? <div className="text-sm italic text-gray-400 dark:text-gray-500">
                                Drop station copies here
                              </div> : group.stations.map(station => <div key={station.id} data-id={station.id} className="flex items-center justify-between rounded border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-800">
                                  <span className="text-sm text-gray-800 dark:text-gray-100">
                                    {station.name}
                                  </span>
                                  <span className="text-xs uppercase text-gray-500 dark:text-gray-400">
                                    {station.role || "station"} #{station.station_id}
                                  </span>
                                </div>)}
                          </ReactSortable>
                        </div>)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}
